import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import theme, { mobileBreakpoint } from '../theme';
import config from '../config';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';

// Keyframes for sparkles
const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(219, 39, 98, 0.4), 0 0 20px rgba(241, 222, 124, 0.3); }
  50% { box-shadow: 0 6px 30px rgba(219, 39, 98, 0.6), 0 0 30px rgba(241, 222, 124, 0.5); }
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: -5px;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto 48px auto;
  padding: 40px 24px 48px 24px;
  background: linear-gradient(135deg, #fff 100%,#fff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.primary};
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const InfoBox = styled.div`
  background: #f5f5f5;
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid #e0e0e0;
  padding: 18px 20px;
  color: #333;
  font-size: 15px;
  margin-top: 10px;
  text-align: center;
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 14px;
  }
`;

const InfoText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  color: ${theme.colors.primary};
`;

const InputLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: ${theme.colors.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 16px;
  font-family: inherit;
  margin-bottom: 16px;
  margin-top: 4px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
  border-radius: ${theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
  border-radius: ${theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const ActionButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #e8316d 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #e8316d 0%, ${theme.colors.secondary} 100%);
    transform: scale(1.01);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin: 32px 0 0 0;
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CancelButton = styled.button`
  margin: 32px auto 0 auto;
  min-width: 300px;
  display: block;
  margin-bottom: -20px;
  padding: 14px 32px;
  background: #eee;
  color: ${theme.colors.primary};
  border: 0px solid ${theme.colors.accent1};
  border-radius: ${theme.borderRadius.medium};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.secondary};
    transform: scale(1.01);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: unset;
    width: 100%;
  }
`;

const TrialLessonCancelPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [invoiceName, setInvoiceName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setError(null);
    setSuccess(false);

    if (!invoiceName.trim() || !email.trim()) {
      setError('Bitte fülle Name und E-Mail-Adresse aus.');
      return;
    }

    setCancelling(true);

    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const viewportWidth = window.innerWidth;
      const response = await fetch(config.getApiUrl(`/api/trial-lessons/cancel-by-name-and-email?viewportWidth=${viewportWidth}`), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          invoiceName: invoiceName.trim(),
          email: email.trim()
        })
      });

      if (response.ok) {
        setSuccess(true);
        // Clear form after successful cancellation
        setTimeout(() => {
          setInvoiceName('');
          setEmail('');
        }, 0);
      } else {
        const errorText = await response.text();
        setError(errorText || 'Fehler beim Stornieren der Probestunde.');
      }
    } catch (err) {
      setError('Netzwerkfehler. Bitte versuche es später erneut.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <PageLayout>
      <Container>
        <Title>Probestunde stornieren</Title>
        
        <Section>
          <InfoText>
              Um deine Probestunde zu stornieren, gib bitte deinen Namen und deine E-Mail-Adresse ein:
            </InfoText>
        </Section>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && (
          <SuccessMessage>
            Probestunde erfolgreich storniert! Du erhältst in Kürze eine Bestätigung per E-Mail.
          </SuccessMessage>
        )}

        <Section>
          <InputLabel htmlFor="invoiceName">
            Name *
          </InputLabel>
          <Input
            id="invoiceName"
            type="text"
            value={invoiceName}
            onChange={(e) => setInvoiceName(e.target.value)}
            placeholder="Name wie bei der Buchung"
            disabled={cancelling || success}
          />

          <InputLabel htmlFor="email">
            E-Mail-Adresse *
          </InputLabel>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-Mail-Adresse wie bei der Buchung"
            disabled={cancelling || success}
          />
        </Section>

        <ButtonContainer>
          <CancelButton 
            onClick={() => navigate('/')}
            disabled={cancelling}
          >
            Abbrechen
          </CancelButton>
          <ActionButton 
            onClick={handleCancel}
            disabled={cancelling || success || !invoiceName.trim() || !email.trim()}
          >
            {cancelling ? 'Storniere...' : 'Probestunde stornieren'}
          </ActionButton>
        </ButtonContainer>
      </Container>
    </PageLayout>
  );
};

export default TrialLessonCancelPage;

